import { gsap } from 'gsap';
import type { ThreeStage } from '../../../three/ThreeStage';
import { BaseScene } from '../../../core/BaseScene';
import { brandLogo } from '../../../components/brandLogo';

/** Concept words that surface briefly around the forming network. Positions are in vw/vh from centre. */
const CONCEPTS: ReadonlyArray<[word: string, x: number, y: number]> = [
  ['AI', -24, -20],
  ['DATA', 21, -26],
  ['AUTOMATION', 28, 9],
  ['CLOUD', -31, 12],
  ['PEOPLE', 3, 30],
  ['PRODUCTS', -9, -33],
  ['WORKFLOWS', 12, 21],
];

/**
 * 0 darkness and a single point
 * 1 points emerge, a network forms, concepts flicker past
 * 2 the network converges and the Moderta logo resolves out of the light
 * 3 Ideas → Systems → Impact
 * 4 Building digital systems around real-world problems.
 */
export class OpeningScene extends BaseScene {
  public readonly stepCount = 5;

  protected template(): string {
    return `
      <div class="opening-concepts" aria-hidden="true">
        ${CONCEPTS.map(([word, x, y]) => `<span style="--x:${x};--y:${y}">${word}</span>`).join('')}
      </div>
      <div class="scene-layout layout-center opening">
        <div class="opening-brand" data-from="2">
          <h1 class="wordmark">${brandLogo('moderta', { height: 150 })}</h1>
          <p class="opening-formula" data-from="3" aria-label="Ideas, systems, impact">
            <span>Ideas</span><i aria-hidden="true"></i><span>Systems</span><i aria-hidden="true"></i><span>Impact</span>
          </p>
          <p class="opening-line" data-from="4">Building digital systems around real-world problems.</p>
        </div>
      </div>`;
  }

  protected onStep(step: number, stage: ThreeStage, immediate: boolean, previousStep: number): void {
    stage.setAccent(this.meta.accent);
    stage.setMode('opening', step, immediate);

    const concepts = this.$$('.opening-concepts span');
    const logo = this.$('.wordmark .brand-logo');
    const formula = this.$$('.opening-formula span, .opening-formula i');
    const line = this.$('.opening-line');
    const forward = !immediate && step > previousStep;
    const tl = this.timeline();

    // Concepts are transient: they only ever play forward during step 1 and never persist.
    gsap.set(concepts, { opacity: 0 });
    if (step === 1 && forward) {
      concepts.forEach((concept, index) => {
        const at = 0.9 + index * 0.48;
        tl.fromTo(concept, { opacity: 0, filter: 'blur(8px)', scale: 0.92 }, { opacity: 0.78, filter: 'blur(0px)', scale: 1, duration: 0.7, ease: 'power2.out' }, at);
        tl.to(concept, { opacity: 0, filter: 'blur(6px)', duration: 0.9, ease: 'power2.in' }, at + 1.1);
      });
    }

    if (step < 2) {
      gsap.set(logo, { opacity: 0 });
    } else if (step === 2 && forward) {
      // Wait for the network to collapse, then let the master logo open from the centre out of the light.
      tl.fromTo(
        logo,
        { opacity: 0, filter: 'blur(14px)', clipPath: 'inset(0 50% 0 50%)', scale: 1.06 },
        { opacity: 1, filter: 'blur(0px)', clipPath: 'inset(0 0% 0 0%)', scale: 1, duration: 1.6, ease: 'expo.out' },
        1.0,
      );
    } else {
      gsap.set(logo, { opacity: 1, filter: 'blur(0px)', clipPath: 'inset(0 0% 0 0%)', scale: 1 });
    }

    if (step === 3 && forward) {
      tl.fromTo(formula, { opacity: 0, y: 14 }, { opacity: 1, y: 0, duration: 0.7, stagger: 0.32, ease: 'power3.out' });
    } else {
      gsap.set(formula, { opacity: 1, y: 0 });
    }

    if (step === 4 && forward) {
      tl.fromTo(line, { opacity: 0, y: 12, filter: 'blur(6px)' }, { opacity: 1, y: 0, filter: 'blur(0px)', duration: 1.1, ease: 'power3.out' }, 0.2);
    } else {
      gsap.set(line, { opacity: 1, y: 0, filter: 'blur(0px)' });
    }
  }
}
